import React from 'react';
import './Privacy.css';
import LightRays from '../../components/common/LightRays/LightRays';
import { SEO, StructuredData } from '../../components/common/SEO';

const Privacy: React.FC = () => {
    return (
        <div className="privacy-page">
            <SEO
                title="Privacy Policy - Upshift | Your Data Protection & Privacy"
                description="Read Upshift's Privacy Policy to understand how we collect, use, and protect your personal information. Your privacy is our priority."
                keywords="upshift privacy policy, data protection, privacy, personal information, user data, GDPR"
                image="https://www.joinupshift.com/icon.png"
                type="website"
                noindex={false}
            />
            <StructuredData
                type="webpage"
                title="Privacy Policy - Upshift"
                description="Upshift Privacy Policy and data protection information"
                url="https://www.joinupshift.com/privacy"
            />
             <LightRays
        raysOrigin="top-center"
        raysColor="#667EEA"
        raysSpeed={0.6}
        lightSpread={1.5}
        rayLength={2}
        pulsating={true}
        fadeDistance={1.2}
        saturation={0.8}
        followMouse={true}
        mouseInfluence={0.1}
        noiseAmount={0.05}
        distortion={0.1}
      />
            <div className="privacy-section">
                <div className="privacy-card">
                    <h2>Privacy Policy</h2>

                    <div className="privacy-text">
                        <p>At Upshift (joinupshift.com), your privacy is our priority. This Privacy Policy outlines how we collect, use, and protect your personal information when you use our productivity and app blocking app. By accessing or using Upshift, you consent to the practices described below.</p>

                        <h3>1. Information We Collect</h3>
                        <p>We collect the following types of information to provide and improve our services:</p>

                        <p><strong>1.1 Personal Information:</strong></p>
                        <p>Account details such as name, email address, and age range (if provided).</p>
                        <p>Payment information for subscription services (processed securely by third-party providers).</p>

                        <p><strong>1.2 Usage Data:</strong></p>
                        <p>App activity, such as feature usage, session duration, and app blocking statistics.</p>
                        <p>Device information, including operating system, app version, IP address, and installed applications (for app blocking functionality).</p>
                        <p>Quest progress, habit tracking data, streaks, and goal achievements.</p>
                        <p>Time tracking data, focus session statistics, and activity logs.</p>
                        <p>Group participation data, leaderboard rankings, and social interactions.</p>

                        <p><strong>1.3 Health and Wellness Data:</strong></p>
                        <p>Screen time and app usage data, including daily usage duration per app, app blocking statistics, and focus session metrics.</p>
                        <p>Habit tracking data related to health and wellness goals you choose to track (e.g., exercise, reading, meditation, sleep, hydration, or other personal health habits).</p>
                        <p>Digital wellness metrics derived from your screen time and habit data, such as productivity scores, streak progress, and category-based XP (e.g., Health, Strength, Knowledge).</p>
                        <p>Goal progress and achievement data related to health and personal development objectives.</p>

                        <p><strong>1.4 Optional Data:</strong></p>
                        <p>Profile photos, and custom themes.</p>
                        <p>Friend codes, affiliate referrals, and group invitations.</p>

                        <h3>2. How We Use Your Information</h3>
                        <p>We use your information to:</p>
                        <p>Provide, personalize, and improve the app's functionality including app blocking features.</p>
                        <p>Track your progress in quests, habits, goals, and provide personalized recommendations.</p>
                        <p>Enable group functionality, leaderboards, and social features.</p>
                        <p>Send notifications about quest reminders, group activities, and achievements.</p>
                        <p>Process affiliate rewards and referral programs.</p>
                        <p>Communicate with you about updates, features, and support.</p>
                        <p>Analyze usage trends to enhance user experience and app blocking effectiveness.</p>
                        <p>Process payments and manage subscriptions.</p>
                        <p>Ensure compliance with our Terms and Conditions.</p>

                        <h3>3. Data Sharing</h3>
                        <p>We do not sell or rent your personal information to third parties. However, we may share information in the following cases:</p>

                        <p><strong>3.1 Service Providers:</strong></p>
                        <p>Trusted third parties that assist with payment processing, analytics, and app hosting.</p>

                        <p><strong>3.2 Legal Compliance:</strong></p>
                        <p>When required to comply with legal obligations or protect the rights, safety, and property of Upshift, its users, or others.</p>

                        <p><strong>3.3 Business Transfers:</strong></p>
                        <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity.</p>

                        <h3>4. Data Security</h3>
                        <p>We implement industry-standard security measures to protect your personal information. However, no method of electronic storage or transmission is 100% secure. While we strive to protect your data, we cannot guarantee its absolute security.</p>

                        <h3>5. Your Rights</h3>
                        <p>Depending on your location, you may have the following rights regarding your data:</p>
                        <p>Access and Portability: Request access to or a copy of your personal data.</p>
                        <p>Correction: Update or correct inaccurate information.</p>
                        <p>Deletion: Request the deletion of your account and associated data.</p>
                        <p>Restriction: Limit the processing of your data in certain circumstances.</p>
                        <p>Withdrawal of Consent: Revoke consent for specific data uses.</p>
                        <p>To exercise these rights, contact us at support&#64;joinupshift.com.</p>

                        <h3>6. Photo and Media Storage</h3>
                        <p>If you choose to upload photos (profile pictures, goal progress photos), these are stored securely and used only for the purposes you specify. You can delete uploaded photos at any time through the app settings.</p>

                        <h3>7. Data Retention</h3>
                        <p>We retain your personal data for as long as necessary to provide the app's services or comply with legal obligations. Upon account deletion, we will securely delete or anonymize your data unless required by law to retain it.</p>
                        <p>Quest progress, habit data, and group participation history may be retained for statistical analysis in anonymized form.</p>

                        <h3>8. Cookies and Tracking Technologies</h3>
                        <p>Upshift uses cookies and similar technologies to:</p>
                        <p>Understand app usage and improve functionality.</p>
                        <p>Deliver a seamless and personalized experience.</p>
                        <p>You can control cookie preferences through your device settings.</p>

                        <h3>9. Health Data</h3>
                        <p>Upshift collects and processes certain data that may be classified as Health Data under applicable data protection regulations and app store policies. This includes:</p>
                        <p><strong>What We Collect:</strong> Screen time and app usage statistics, habit tracking data (including health-related habits such as exercise, sleep, hydration, and meditation), digital wellness scores, focus session metrics, streak data, and category-based progress data (e.g., Health, Strength, Knowledge XP).</p>
                        <p><strong>How We Collect It:</strong> Health data is collected directly from your interactions within the app, such as when you create and track habits, log focus sessions, or use the app blocking feature. On iOS, screen time data is accessed through Apple's Screen Time API. On Android, usage data is collected through the Usage Stats permission. We do not access data from external health apps or devices unless you explicitly connect them.</p>
                        <p><strong>How We Use It:</strong> We use health data solely to provide and improve your in-app experience, including displaying your progress, generating personalized insights and recommendations, powering quest and streak systems, enabling group challenges and leaderboards, and delivering relevant notifications about your goals.</p>
                        <p><strong>How We Protect It:</strong> Health data is encrypted in transit and at rest. We do not sell, rent, or share your health data with third parties for advertising or marketing purposes. Access to health data within our organization is limited to personnel who require it to maintain and improve the service.</p>
                        <p><strong>Your Control:</strong> You can view, export, or delete your health data at any time through the app settings or by contacting us at support&#64;joinupshift.com. Upon account deletion, all health data is permanently removed from our systems unless retention is required by law.</p>

                        <h3>10. Third-Party Links</h3>
                        <p>Our app may include links to third-party websites or services. We are not responsible for the privacy practices or content of these third parties. We encourage you to review their privacy policies before providing any information.</p>

                        <h3>11. TikTok Login Kit</h3>
                        <p>Upshift (joinupshift.com) uses TikTok Login Kit to allow users to sign in with their TikTok account. When you choose to log in via TikTok, we may receive your TikTok username and profile picture. This information is used solely to create and identify your Upshift account. We do not post to TikTok on your behalf, access your TikTok content, or share your TikTok data with third parties.</p>

                        <h3>12. Updates to this Privacy Policy</h3>
                        <p>We may update this Privacy Policy periodically to reflect changes in our practices or for legal reasons. We will notify you of significant updates and encourage you to review this document regularly.</p>
                    </div>
                </div>
            </div>

            {/* Last Updated */}
            <div className="last-updated">
                <p>Last updated: August 2026</p>
            </div>
        </div>
    );
};

export default Privacy;
